import { QueryParams } from '../../../types/baseExplorerConfig';
import { logger } from '../../../utils/logger';

const log = logger('VARServiceAdapter');

/**
 * Translation of explorer query params into the flat key/value shape the VAR
 * backend expects.
 *
 * Kept in its own module so the rules can be tested against the real code
 * rather than a copy: the adapter test rebuilt the URL inline to avoid the
 * adapter's configService dependency, and that copy drifted -- it was missing
 * the details, var_subtype and verification pass-through fields. #839, after
 * the same fix for lightning in #836.
 *
 * URL assembly stays in the adapter, which needs getBaseUrl().
 */

const FILTER_FIELD_MAP: Record<string, string> = {
  sprinklerFlag: 'sprinkler_active',
};

const PASS_THROUGH_FIELDS = new Set([
  'var_type', 'status', 'region', 'occupancy', 'red_flag_active',
  'var_id', 'name', 'details', 'var_subtype', 'keywords', 'verification',
]);

/**
 * Translate CBE filter/search/sort params into the flat key/value shape the
 * VAR backend expects. Single source of truth used by both fetchData and
 * buildExportParams so any filter that works in the view automatically
 * works in the export.
 */
export function translateVarParams(params: QueryParams): Record<string, any> {
  const out: Record<string, any> = {};

  if (params?.search?.term) {
    out.search = params.search.term;
  }

  if (params?.sort?.[0]) {
    out.sortBy = params.sort[0].field;
    out.sortOrder = params.sort[0].direction;
  }

  if (!params?.filters) return out;

  for (const filter of params.filters) {
    const mappedKey = FILTER_FIELD_MAP[filter.field];
    if (mappedKey) {
      out[mappedKey] = filter.value;
      continue;
    }
    if (PASS_THROUGH_FIELDS.has(filter.field)) {
      out[filter.field] = filter.value;
      continue;
    }
    log.warn(`VAR ServiceAdapter: Unsupported filter field: ${filter.field}`);
  }

  return out;
}
